import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { marked } from "marked";
import { SiteLayout } from "@/components/site/layout";
import { Reveal } from "@/components/site/reveal";
import { localBusinessJsonLd } from "@/components/site/jsonld";
import { getPublishedArticleBySlug } from "@/lib/synlig";
import { SITE } from "@/lib/site";

export const Route = createFileRoute("/blogg/$slug")({
  loader: async ({ params }) => {
    const article = await getPublishedArticleBySlug(params.slug);
    if (!article) throw notFound();
    return article;
  },
  head: ({ loaderData }) => ({
    meta: loaderData
      ? [
          { title: `${loaderData.title} | Drammen Car Wash` },
          { name: "description", content: loaderData.meta_description ?? "" },
          { property: "og:title", content: loaderData.title },
          { property: "og:description", content: loaderData.meta_description ?? "" },
          { property: "og:url", content: `${SITE.url}/blogg/${loaderData.slug}` },
        ]
      : [],
    links: loaderData
      ? [{ rel: "canonical", href: `${SITE.url}/blogg/${loaderData.slug}` }]
      : [],
    scripts: loaderData
      ? [{ type: "application/ld+json", children: JSON.stringify(localBusinessJsonLd()) }]
      : [],
  }),
  component: BloggArticle,
});

function BloggArticle() {
  const article = Route.useLoaderData();

  return (
    <SiteLayout>
      <article className="pt-32 pb-20 sm:pt-40 sm:pb-28">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-10">
          <Reveal>
            <Link
              to="/blogg"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Tilbake til bloggen
            </Link>
            <h1 className="mt-6 font-serif text-3xl sm:text-4xl md:text-5xl text-balance">
              {article.title}
            </h1>
          </Reveal>
          <Reveal delay={80}>
            <div
              className="mt-10 max-w-none space-y-4 text-foreground/85 leading-relaxed [&_h2]:font-serif [&_h2]:text-2xl [&_h2]:mt-10 [&_h2]:mb-2 [&_h3]:font-serif [&_h3]:text-xl [&_h3]:mt-8 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_a]:text-primary [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: marked.parse(article.draft_raw, { async: false }) }}
            />
          </Reveal>
          <Reveal delay={120} className="mt-12 flex justify-center">
            <Link
              to="/book"
              className="inline-flex items-center gap-2 rounded-full bg-primary-strong px-7 py-3.5 text-sm font-medium text-primary-foreground shadow-luxe lift"
            >
              Book time <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </article>
    </SiteLayout>
  );
}
